import React from 'react';
import {
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Typography,
    Box,
    Chip,
    IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatNotificationTime } from '../../GCPortal/utils/dateUtils';
import CheckIcon from '@mui/icons-material/Check';
import { NOTIFICATION_EVENTS } from '../constants';

// Import icons for different event types
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CommentIcon from '@mui/icons-material/Comment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

/**
 * Styled components
 */
const StyledListItem = styled(ListItem)(({ theme, isUnread }) => ({
    padding: theme.spacing(2, 3),
    cursor: 'pointer',
    backgroundColor: isUnread ? theme.palette.grey[50] : 'transparent',
    borderLeft: isUnread ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
    transition: 'background-color 0.2s ease',
    '&:hover': {
        backgroundColor: theme.palette.grey[100],
    },
}));

const UnreadDot = styled(Box)(({ theme }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    marginLeft: theme.spacing(1),
    flexShrink: 0,
}));

/**
 * Get icon and color for different event types
 */
const getEventIcon = (eventKey) => {
    switch (eventKey) {
        case NOTIFICATION_EVENTS.INTERNAL_ESCALATION:
        case NOTIFICATION_EVENTS.EXTERNAL_ESCALATION:
            return <PriorityHighIcon sx={{ color: '#d32f2f', fontSize: 20 }} />;
        case NOTIFICATION_EVENTS.DUEDATE_REMINDER:
            return <WarningIcon sx={{ color: '#f57c00', fontSize: 20 }} />;
        case NOTIFICATION_EVENTS.SUBMITTED_DEF:
            return <AssignmentIcon sx={{ color: '#1976d2', fontSize: 20 }} />;
        case NOTIFICATION_EVENTS.WORK_COMPLETED:
        case NOTIFICATION_EVENTS.WORK_APPROVED:
            return <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 20 }} />;
        case NOTIFICATION_EVENTS.EXTERNAL_ASSIGNED:
        case NOTIFICATION_EVENTS.EXTERNAL_REASSIGNED:
            return <AssignmentIcon sx={{ color: '#1976d2', fontSize: 20 }} />;
        case NOTIFICATION_EVENTS.DUEDATE_UPDATE:
            return <EditIcon sx={{ color: '#f57c00', fontSize: 20 }} />;
        case NOTIFICATION_EVENTS.INTERNAL_REJECTION:
        case NOTIFICATION_EVENTS.EXTERNAL_REJECTION:
            return <CloseIcon sx={{ color: '#d32f2f', fontSize: 20 }} />;
        case NOTIFICATION_EVENTS.EXTERNALLY_SIGNED_OFF:
            return <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 20 }} />;
        case NOTIFICATION_EVENTS.COMMENT_ADDED:
            return <CommentIcon sx={{ color: '#1976d2', fontSize: 20 }} />;
        case NOTIFICATION_EVENTS.FIELD_WORKER_REQUEST_SUBMITTED:
            return <PersonAddIcon sx={{ color: '#1976d2', fontSize: 20 }} />;
        case NOTIFICATION_EVENTS.FIELD_WORKER_REQUEST_APPROVED:
            return <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 20 }} />;
        case NOTIFICATION_EVENTS.FIELD_WORKER_REQUEST_REJECTED:
            return <CloseIcon sx={{ color: '#d32f2f', fontSize: 20 }} />;
        default:
            return <NotificationImportantIcon sx={{ color: '#757575', fontSize: 20 }} />;
    }
};

/**
 * Get priority chip for high-priority events
 */
const getPriorityChip = (eventKey) => {
    const highPriorityEvents = [
        NOTIFICATION_EVENTS.INTERNAL_REJECTION,
        NOTIFICATION_EVENTS.EXTERNAL_REJECTION,
        NOTIFICATION_EVENTS.INTERNAL_ESCALATION,
        NOTIFICATION_EVENTS.EXTERNAL_ESCALATION,
        NOTIFICATION_EVENTS.DUEDATE_REMINDER,
        NOTIFICATION_EVENTS.FIELD_WORKER_REQUEST_SUBMITTED,
    ];

    if (highPriorityEvents.includes(eventKey)) {
        return (
            <Chip
                label="Priority"
                size="small"
                sx={{
                    backgroundColor: '#FEF3C7',
                    color: '#92400E',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: 20,
                }}
            />
        );
    }

    return null;
};

/**
 * Generate notification message based on event type and payload
 */
const generateMessage = (notification) => {
    const {
        eventKey,
        payload: payloadString,
        actorName,
        deficiencyTitle,
        entityTitle,
    } = notification;

    // Parse the payload JSON string
    let payload = {};
    try {
        if (payloadString && typeof payloadString === 'string') {
            payload = JSON.parse(payloadString);
        }
    } catch (error) {
        // Silently handle payload parsing errors
    }

    // Get actor name and entity info - available at top level or in payload
    const actor = actorName || payload.actorName || 'Someone';
    const title = entityTitle || payload.entityTitle || deficiencyTitle || payload.deficiencyTitle;

    // Use backend message if available (it contains the proper actor name)
    const backendMessage = payload.message;

    switch (eventKey) {
        case NOTIFICATION_EVENTS.EXTERNAL_ASSIGNED:
            return backendMessage || `${actor} assigned deficiency '${title}' to you`;
        case NOTIFICATION_EVENTS.EXTERNAL_REASSIGNED:
            return backendMessage || `${actor} reassigned deficiency '${title}'`;
        case NOTIFICATION_EVENTS.DUEDATE_UPDATE:
            return backendMessage || `${actor} updated the due date for deficiency '${title}'`;
        case NOTIFICATION_EVENTS.INTERNAL_REJECTION:
            return backendMessage || `${actor} rejected your work on deficiency '${title}'`;
        case NOTIFICATION_EVENTS.EXTERNAL_REJECTION:
            return backendMessage || `${actor} rejected the completion of deficiency '${title}'`;
        case NOTIFICATION_EVENTS.WORK_COMPLETED:
            return backendMessage || `${actor} marked deficiency '${title}' as work completed`;
        case NOTIFICATION_EVENTS.WORK_APPROVED:
            return backendMessage || `${actor} fully approved your work on deficiency '${title}'`;
        case NOTIFICATION_EVENTS.SUBMITTED_DEF:
            return backendMessage || `${actor} submitted a new deficiency '${title}'`;
        case NOTIFICATION_EVENTS.EXTERNALLY_SIGNED_OFF:
            return backendMessage || `${actor} signed off on deficiency '${title}'`;
        case NOTIFICATION_EVENTS.DUEDATE_REMINDER:
            return `Deficiency '${title}' is due soon`;
        case NOTIFICATION_EVENTS.INTERNAL_ESCALATION:
            return `Deficiency '${title}' is overdue and needs attention`;
        case NOTIFICATION_EVENTS.EXTERNAL_ESCALATION:
            return `Deficiency '${title}' is overdue and needs attention`;
        case NOTIFICATION_EVENTS.COMMENT_ADDED:
            return backendMessage || `${actor} added a comment to deficiency '${title}'`;
        case NOTIFICATION_EVENTS.FIELD_WORKER_REQUEST_SUBMITTED:
            return backendMessage || `${actor} submitted a request to add a crew member`;
        case NOTIFICATION_EVENTS.FIELD_WORKER_REQUEST_APPROVED:
            return backendMessage || `${actor} approved a crew member request`;
        case NOTIFICATION_EVENTS.FIELD_WORKER_REQUEST_REJECTED:
            return backendMessage || `${actor} rejected a crew member request`;
        default:
            return notification.message || `Update on '${title}'`;
    }
};

/**
 * NotificationItem Component
 * Individual notification list item with click-to-navigate functionality
 */
const NotificationItem = ({ notification, onMarkAsRead, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const icon = getEventIcon(notification.eventKey);
    const priorityChip = getPriorityChip(notification.eventKey);
    const message = generateMessage(notification);

    // Entity navigation - currently supports deficiencies, can be extended for other entity types
    const entityType = notification.entityType || 'DEFICIENCY';
    const entityId = notification.entityId;
    const deficiencyId = entityType === 'DEFICIENCY' ? entityId : null;

    // Format timestamp - backend now sends proper UTC LocalDateTime
    const timeAgo = formatNotificationTime(notification.createdAt || notification.timestamp);

    // Rejection notifications should land the user on the rejection comment.
    const scrollToRejection = notification.eventKey === NOTIFICATION_EVENTS.WORK_REJECTED;

    // The exact attempt that was rejected, so we can scroll to its specific comment
    // (a deficiency may have multiple rejection cycles). Falls back to latest if absent.
    let rejectionAttemptId = null;
    if (scrollToRejection && typeof notification.payload === 'string') {
        try {
            rejectionAttemptId = JSON.parse(notification.payload).rejectedAttemptId || null;
        } catch (error) {
            // Ignore malformed payload; fall back to the latest rejection comment.
        }
    }

    const handleClick = () => {
        // Mark as read if unread - backend handles resolution automatically
        if (!notification.viewed) {
            onMarkAsRead([notification.notificationId]);
        }

        // Navigate based on entity type
        if (entityType === 'DEFICIENCY' && deficiencyId) {
            // Extract project ID from notification
            const projectId = notification.projectId;

            if (projectId) {
                // Check if we're already on the correct deficiency page
                const expectedPath = `/projects/${projectId}/deficiencies`;
                const isOnCorrectPage = location.pathname === expectedPath;

                // Close the dropdown before any action
                if (onClose) {
                    onClose();
                }

                if (isOnCorrectPage) {
                    // We're already on the deficiency page, trigger sidebar opening directly
                    // Create a custom event that the DeficiencyPage can listen to
                    const openSidebarEvent = new CustomEvent('openDeficiencySidebar', {
                        detail: {
                            deficiencyId,
                            notificationId: notification.notificationId,
                            scrollToRejection,
                            rejectionAttemptId,
                        },
                    });
                    window.dispatchEvent(openSidebarEvent);
                } else {
                    // Navigate to the deficiency page
                    navigate(expectedPath, {
                        state: {
                            highlightDeficiency: deficiencyId,
                            scrollToRejection,
                            rejectionAttemptId,
                        },
                    });
                }
            }
        }
        if (entityType === 'FIELD_WORKER_REQUEST') {
            const projectId = notification.projectId;
            if (projectId) {
                if (onClose) {
                    onClose();
                }
                navigate(`/projects/${projectId}/settings`, {
                    state: { selectedTab: 'field-workers' },
                });
            }
        }
    };

    const handleMarkAsRead = (event) => {
        event.stopPropagation();
        onMarkAsRead([notification.notificationId]);
    };

    return (
        <StyledListItem isUnread={!notification.viewed} onClick={handleClick}>
            <ListItemAvatar>
                <Avatar
                    sx={{
                        backgroundColor: '#f5f5f5',
                        width: 36,
                        height: 36,
                    }}
                >
                    {icon}
                </Avatar>
            </ListItemAvatar>

            <ListItemText
                primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: notification.viewed ? 400 : 600,
                                color: 'text.primary',
                                lineHeight: 1.4,
                            }}
                        >
                            {message}
                        </Typography>
                        {!notification.viewed && <UnreadDot />}
                    </Box>
                }
                secondary={
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mt: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                {timeAgo}
                            </Typography>
                            {priorityChip}
                        </Box>

                        {!notification.viewed && (
                            <IconButton
                                size="small"
                                onClick={handleMarkAsRead}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'primary.light',
                                    },
                                }}
                            >
                                <CheckIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                }
            />
        </StyledListItem>
    );
};

export default NotificationItem;
